import { useEffect, useRef, useState, type FormEvent } from "react";
import { CheckCircle2, Inbox, RefreshCw, Search, ArrowUpRight } from "lucide-react";
import Dialog from "./Dialog";
import { EmptyState, ErrorState } from "./Ui";
import { AGENCY_TZ } from "../api";
import { passwordValidationError } from "../password-validation";
import "./PortalRequests.css";

/** One request as the portal sees it (routes/tasks.js GET /client-requests). */
export type PortalRequest = {
  id: number;
  title: string;
  details: string;
  request_type: string | null;
  urgent: boolean;
  /** The work queue status: pending, in_progress, escalated, snoozed, completed. */
  state: string;
  /** That status in the client's words: Open, In progress, On hold, Completed. */
  status: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
};
type Send = (path: string, body?: Record<string, unknown>, method?: string) => Promise<any>;

export const REQUEST_TYPES = ["Google Ads", "SEO", "Website", "Reporting", "Billing", "Other"];

const TONE: Record<string, string> = {
  pending: "chip-neutral",
  in_progress: "chip-info",
  escalated: "chip-info",
  snoozed: "chip-warning",
  completed: "chip-success",
};

const STATUS_DETAIL: Record<string, string> = {
  pending: "Received by your team and waiting for review.",
  in_progress: "Your team is working on this request.",
  escalated: "Your team is reviewing this request for extra attention.",
  snoozed: "Work is on hold. Contact your team if you have information that can help.",
  completed: "Your team has marked this request complete. Send a new request if you need anything else.",
};

function RequestStatus({ row }: { row: PortalRequest }) {
  return <span className={`chip chip-sm ${TONE[row.state] || "chip-neutral"}`} title={STATUS_DETAIL[row.state]}>
    {row.state === "completed" && <CheckCircle2 aria-hidden="true" />}{row.status}
  </span>;
}

/** A useful starting point even when reporting is still being connected. */
export function RequestOverview({ rows, loading, error, onOpen, onNew, canSend, onRetry }: {
  rows: PortalRequest[] | null;
  loading: boolean;
  error: string;
  onOpen: (id?: number) => void;
  onNew: () => void;
  canSend: boolean;
  onRetry: () => void;
}) {
  const open = rows?.filter(row => row.state !== "completed") || [];
  const recent = [...(rows || [])].sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime())[0];
  return <section className="panel portal-request-overview" aria-labelledby="portal-request-overview-title">
    <div className="portal-request-overview-heading">
      <div><h2 id="portal-request-overview-title">Your requests</h2><p>{loading && !rows ? "Loading your requests…" : error && !rows ? "Your requests could not be loaded." : !rows?.length ? "Ask your team for a change or help with your account." : open.length ? `${open.length} open ${open.length === 1 ? "request" : "requests"}.` : "Your recent requests are complete."}</p></div>
      {error && !rows ? <button className="button secondary" onClick={onRetry}>Retry requests</button> : <button className="button secondary" onClick={() => !rows?.length && canSend && !loading ? onNew() : onOpen()}>{!rows?.length && canSend && !loading ? "Send a request" : "View requests"}</button>}
    </div>
    {recent && <button className="portal-latest-request" onClick={() => onOpen(recent.id)}><span><small>Latest update</small><strong>{recent.title}</strong><time dateTime={recent.updated_at || recent.created_at}>{sentAt(recent.updated_at || recent.created_at)}</time></span><RequestStatus row={recent}/><ArrowUpRight aria-hidden="true"/></button>}
    {error && rows && <p className="portal-request-stale" role="status">Showing the last loaded requests. <button className="text-button" onClick={onRetry}>Try refreshing</button></p>}
  </section>;
}

/** "Sep 26, 2:14 PM ET", with the year only when it is not this year. Times are the agency's (Eastern). */
export function sentAt(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
    timeZone: AGENCY_TZ,
  })} ET`;
}

/** The client's "New request": what they want, sent straight to the agency's work queue. */
export function NewRequestDialog({ send, onClose, onSent }: { send: Send; onClose: () => void; onSent: (r: { task_id: number }) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending.current) return;
    const f = new FormData(e.currentTarget);
    const subject = String(f.get("subject") || "").trim();
    const description = String(f.get("description") || "").trim();
    if (!subject || !description) {
      setError("Enter a subject and details for your request.");
      return;
    }
    sending.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await send(
        "/api/tasks/client-request",
        {
          subject,
          description,
          request_type: String(f.get("request_type") || "Other"),
          urgent: f.get("urgent") === "on",
        },
        "POST",
      );
      if (mounted.current) onSent(r);
    } catch (err) {
      if (mounted.current) {
        setError(err instanceof Error ? err.message : "Your request could not be sent");
        setBusy(false);
      }
      sending.current = false;
    }
  }
  return (
    <Dialog title="New request" description="Goes straight to the No Limits team. You can follow its status here." onClose={() => { if (!sending.current) onClose(); }}>
      <form className="operation-form record-form" onSubmit={submit} aria-busy={busy}>
        <label>
          <span className="field-label">Subject<span className="field-required" aria-hidden="true"> *</span></span>
          <input name="subject" required maxLength={200} disabled={busy} placeholder="Add our new location to Google Ads" />
        </label>
        <label>
          <span className="field-label">Details<span className="field-required" aria-hidden="true"> *</span></span>
          <textarea name="description" required maxLength={5000} rows={5} disabled={busy} placeholder="What you need, any links, and when you need it by." />
        </label>
        <div className="form-pair">
          <label>
            <span className="field-label">Type</span>
            <select name="request_type" defaultValue="Other" disabled={busy}>
              {REQUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="request-urgent">
            <input type="checkbox" name="urgent" role="switch" disabled={busy} />
            <span>
              <strong>Urgent</strong>
              <small className="field-hint">Flags it for the team to look at first.</small>
            </span>
          </label>
        </div>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button primary" disabled={busy}>{busy ? "Sending…" : "Send request"}</button>
        </div>
      </form>
    </Dialog>
  );
}

/** The client's own requests, newest first, each with its live status. */
export function RequestList({
  rows,
  loading,
  error,
  onRetry,
  canSend,
  highlight,
  selectedRequestId,
  onSelectRequest,
}: {
  rows: PortalRequest[] | null;
  loading: boolean;
  error: string;
  onRetry: () => void;
  canSend: boolean;
  highlight: number | null;
  selectedRequestId?: number | null;
  onSelectRequest?: (id: number | null) => void;
}) {
  const [filter,setFilter] = useState('all');
  const [search,setSearch] = useState('');
  const [localSelectedId,setLocalSelectedId] = useState<number|null>(null);
  const selectedId = selectedRequestId === undefined ? localSelectedId : selectedRequestId;
  function selectRequest(id: number | null) { if (onSelectRequest) onSelectRequest(id); else setLocalSelectedId(id); }
  const selected = rows?.find(row => row.id === selectedId);
  const visible = (rows||[]).filter(r=>(filter==='all'||(filter==='completed'?r.state==='completed':r.state!=='completed'))&&`${r.title} ${r.details} ${r.request_type||''}`.toLowerCase().includes(search.trim().toLowerCase()));
  // An empty tab says what it holds; search wording only once something is typed.
  const emptyLine = search.trim() ? `No ${filter === 'all' ? '' : `${filter} `}requests match your search.` : filter === 'completed' ? 'No completed requests yet.' : 'No open requests right now.';
  // The page title and "New request" live in the portal's header and top bar; the card holds the list.
  return (
    <section className="panel portal-requests" aria-label="Your requests">
      <div className="requests-toolbar"><div className="category-tabs" aria-label="Request filters">{[['all','All requests'],['open','Open'],['completed','Completed']].map(([id,label])=><button key={id} className={filter===id?'active':''} aria-pressed={filter===id} onClick={()=>setFilter(id)}>{label}<span>{(rows||[]).filter(r=>id==='all'||(id==='completed'?r.state==='completed':r.state!=='completed')).length}</span></button>)}</div><div className="requests-toolbar-end"><label className="settings-search"><Search aria-hidden="true"/><span className="sr-only">Search requests</span><input placeholder="Search requests" value={search} onChange={e=>setSearch(e.target.value)}/></label><button className="button secondary" onClick={onRetry} disabled={loading}><RefreshCw aria-hidden="true" />{loading ? "Refreshing…" : "Refresh"}</button></div></div>
      {error && rows && <p className="inline-error request-refresh-error" role="alert">Could not refresh your requests. Showing the last loaded list.</p>}
      {selectedRequestId && !loading && !error && rows && !selected && <p className="inline-notice request-link-notice" role="status">This request is not in this workspace’s recent requests. <button className="text-button" onClick={() => selectRequest(null)}>Dismiss</button></p>}
      {error && !rows ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : loading && !rows ? (
        <p className="workflow-note" role="status">Loading your requests…</p>
      ) : !rows?.length ? (
        <EmptyState
          icon={<Inbox aria-hidden="true" />}
          title="No requests yet"
          description={canSend ? "Need something changed, fixed or looked at? Send it as a new request and follow it here." : "Requests this client sends from their portal show here."}
        />
      ) : !visible.length ? <p className="portal-requests-empty" role="status">{emptyLine}</p> : (
        <table className="portal-table portal-request-table">
          <thead><tr><th scope="col">Request</th><th scope="col">Status</th><th scope="col" className="request-updated-column">Last updated</th></tr></thead>
          <tbody>
          {visible.map((r) => (
            <tr key={r.id} className={r.id === highlight ? "request-table-row just-sent" : "request-table-row"} onClick={() => selectRequest(r.id)}>
              <td>
                <button className="request-title request-open" onClick={event => { event.stopPropagation(); selectRequest(r.id); }}>{r.title}<ArrowUpRight aria-hidden="true"/></button>
                {r.details && <p className="request-details">{r.details}</p>}
                <div className="request-meta">
                  {r.request_type && <span>{r.request_type}</span>}
                  <span>#{r.id}</span>
                  {r.urgent && r.state !== "completed" && <span className="request-urgent-label">Urgent</span>}
                  <span className="request-mobile-updated">Updated <time dateTime={r.updated_at || r.created_at}>{sentAt(r.updated_at || r.created_at)}</time></span>
                </div>
              </td>
              <td><RequestStatus row={r}/></td>
              <td className="request-updated-column"><time dateTime={r.updated_at || r.created_at}>{sentAt(r.updated_at || r.created_at)}</time></td>
            </tr>
          ))}
          </tbody>
        </table>
      )}
      {rows && rows.length >= 50 && <p className="portal-requests-note">Showing your 50 most recent requests.</p>}
      {selected&&<Dialog title={selected.title} description={`Request #${selected.id}, ${selected.request_type||'Other'}`} onClose={()=>selectRequest(null)}><div className="request-detail"><div className="request-detail-status"><RequestStatus row={selected}/>{selected.urgent&&selected.state!=="completed"&&<span className="request-urgent-label">Urgent</span>}</div>{STATUS_DETAIL[selected.state]&&<p className="request-status-explanation">{STATUS_DETAIL[selected.state]}</p>}<h3>Your request</h3><p className="request-detail-body">{selected.details}</p><dl><div><dt>Sent</dt><dd>{sentAt(selected.created_at)}</dd></div><div><dt>Last updated</dt><dd>{sentAt(selected.updated_at || selected.created_at)}</dd></div>{selected.completed_at&&selected.state==="completed"&&<div><dt>Completed</dt><dd>{sentAt(selected.completed_at)}</dd></div>}</dl></div></Dialog>}
    </section>
  );
}

/** A client login's own password change (routes/auth.js POST /change-password). */
export function ChangePasswordDialog({ send, onClose, onDone }: { send: Send; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (saving.current) return;
    const f = new FormData(e.currentTarget);
    const next = String(f.get("new_password") || "");
    const validationError = passwordValidationError(next);
    if (validationError) { setError(validationError); return; }
    if (next !== String(f.get("confirm") || "")) {
      setError("The new passwords do not match.");
      return;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      await send("/api/auth/change-password", { current_password: String(f.get("current_password") || ""), new_password: next }, "POST");
      if (mounted.current) onDone();
    } catch (err) {
      if (mounted.current) {
        setError(err instanceof Error ? err.message : "Password could not be changed");
        setBusy(false);
      }
      saving.current = false;
    }
  }
  return (
    <Dialog title="Change password" description="Signs out your other sessions. This one stays signed in." onClose={() => { if (!saving.current) onClose(); }}>
      <form className="operation-form record-form" onSubmit={submit} aria-busy={busy}>
        <label>
          <span className="field-label">Current password</span>
          <input name="current_password" type="password" autoComplete="current-password" disabled={busy} required />
        </label>
        <div className="form-pair">
          <label>
            <span className="field-label">New password</span>
            <input name="new_password" type="password" autoComplete="new-password" minLength={12} maxLength={72} disabled={busy} required />
            <small className="field-hint">At least 12 characters.</small>
          </label>
          <label>
            <span className="field-label">Repeat new password</span>
            <input name="confirm" type="password" autoComplete="new-password" minLength={12} maxLength={72} disabled={busy} required />
          </label>
        </div>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button primary" disabled={busy}>{busy ? "Saving…" : "Change password"}</button>
        </div>
      </form>
    </Dialog>
  );
}
